import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, Menu, X, Users, Building2, Pill, UserCheck, Mail, Phone, MapPin, 
  Award, ShieldCheck, ChevronRight, ChevronDown, LogOut, LayoutDashboard, 
  TrendingUp, AlertCircle, CheckCircle2, Clock, Activity, BarChart2, Users2, 
  Settings2, FlaskConical, HeartPulse, FileSpreadsheet, FolderKanban, Stethoscope,
  Inbox, Send, Layers, CreditCard, Radio, FileText, UserPlus, SlidersHorizontal,
  Globe
} from 'lucide-react';
import './dashboard.css';

// Import components
import ClinicDetails from './Clinic/ClinicDetails';
import ClinicForm from './Clinic/ClinicForm';
import DoctorDetails from './Doctor/DoctorDetails';
import DoctorForm from './Doctor/DoctorForm';
import PharmacyDetails from './Pharmacy/PharmacyDetails';
import PharmacyForm from './Pharmacy/PharmacyForm';
import SupervisorDetails from './Supervisor/SupervisorDetails';
import SupervisorForm from './Supervisor/SupervisorForm';
import CustomAlert from './CustomAlert';
import Analytics from './Analytics';
import SpecialityMaster from './Speciality/SpecialityMaster';
import PharmacyCategoryMaster from './ShopCategories/PharmacyCategoryMaster';
import LabTestCategoryMaster from './ShopCategories/LabTestCategoryMaster';
import HomeCareCategoryMaster from './ShopCategories/HomeCareCategoryMaster';
import UserManagement from './UserManagement';
import RoleMaster from './RoleMaster';
import RevenueReport from './RevenueReport';
import Settings from './Settings';
import BulkUploadModal from './BulkUploadModal';
import LeadsManagement from './Leads/LeadsManagement';
import SubscriptionModuleMaster from './Subscription/SubscriptionModuleMaster';
import VendorIntegrationManagement from './Vendor/VendorIntegrationManagement';
import CurrencyMaster from './Currency/CurrencyMaster';
import LocationMaster from './Location/LocationMaster';
import VendorAccessModal from './Vendor/VendorAccessModal';
import PlanAssignmentPanel from './Subscription/PlanAssignmentPanel';
import ServiceVendorForm from './ServiceVendor/ServiceVendorForm';

// Import API services
import ClinicService from './Clinic/ClinicService';
import DoctorService from './Doctor/DoctorService';
import PharmacyService from './Pharmacy/PharmacyService';
import SupervisorService from './Supervisor/SupervisorService';

import Cookies from 'js-cookie';
import { apiFetch, AdminService } from '../services/api';

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001'}/admin`;

const OrganisationDataGrid = ({ data, activeSection, clinics, page, pageSize, onPageChange, onPageSizeChange, onOpen }) => {
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const visible = data.slice(start, start + pageSize);
  const detailsFor = item => {
    const type = (item.vendorTypeLabel || activeSection).toLowerCase();
    if (type === 'clinic') return item.reg_no ? `Reg: ${item.reg_no}` : 'Registration not provided';
    if (type === 'doctor') return item.specialty || item.education || 'General practice';
    if (type === 'pharmacy') return item.reg_no ? `Reg: ${item.reg_no}` : (item.clinic || 'Independent pharmacy');
    return `${item.role || 'Supervisor'} · ${item.department || 'General'}`;
  };
  const associationFor = item => {
    const type = (item.vendorTypeLabel || activeSection).toLowerCase();
    if (type === 'clinic') return item.vendor_location || item.vendor_address || 'N/A';
    if (type === 'doctor') return item.clinic || clinics.find(c => Number(c.id) === Number(item.clinic_id))?.vendor_name || 'Independent';
    if (type === 'pharmacy') return item.clinic || clinics.find(c => Number(c.id) === Number(item.clinic_id))?.vendor_name || item.location || item.vendor_location || 'Independent';
    return clinics.find(c => Number(c.id) === Number(item.clinic_id))?.vendor_name || item.clinic || 'Platform';
  };
  const modelFor = item => {
    const type = (item.vendorTypeLabel || activeSection).toLowerCase();
    if (type === 'clinic') return item.billing_model === 'commission' ? `Commission ${item.commission_rate || 0}${item.commission_type === 'percentage' ? '%' : ''}` : `Subscription · ${item.subscription_type || 'Monthly'}`;
    if (type === 'doctor') return item.is_committed === 1 || item.commitment_type === 'committed' ? 'Assured · Committed' : 'Normal doctor';
    return item.business_model ? item.business_model.replaceAll('_', ' ') : 'CMS';
  };

  return <div className="org-grid-shell">
    <div className="org-grid-toolbar">
      <span>Showing <strong>{data.length ? start + 1 : 0}-{Math.min(start + pageSize, data.length)}</strong> of <strong>{data.length}</strong> records</span>
      <label>Rows per page<select value={pageSize} onChange={event => onPageSizeChange(Number(event.target.value))}><option value="10">10</option><option value="25">25</option><option value="50">50</option></select></label>
    </div>
    <div className="org-grid-scroll"><table className="org-data-grid"><thead><tr><th>Organisation / Person</th><th>Type</th><th>Contact</th><th>Location / Association</th><th>Registration / Role</th><th>Model</th><th>Status</th><th aria-label="Actions"></th></tr></thead><tbody>
      {visible.map(item => {
        const name = item.vendor_name || item.vendorName || 'Unnamed';
        const type = item.vendorTypeLabel || activeSection;
        const approved = item.vendor_approve_status === 'A';
        return <tr key={`${type}-${item.id}`} onClick={() => onOpen(item)}>
          <td><div className="org-name-cell">{item.vendor_profile_path ? <img src={item.vendor_profile_path} alt="" /> : <span>{name.charAt(0).toUpperCase()}</span>}<div><strong>{name}</strong><small>ID #{item.id}</small></div></div></td>
          <td><span className={`org-type-badge ${String(type).toLowerCase()}`}>{type}</span></td>
          <td><div className="org-contact-cell"><span>{item.vendor_email || item.vendorEmail || 'N/A'}</span><small>{item.vendor_phone || item.vendorConNumber || 'N/A'}</small></div></td>
          <td><span className="org-truncate" title={associationFor(item)}>{associationFor(item)}</span></td>
          <td><span className="org-truncate" title={detailsFor(item)}>{detailsFor(item)}</span></td>
          <td><span className="org-model-text">{modelFor(item)}</span></td>
          <td><span className={`org-status ${approved ? 'approved' : 'pending'}`}><i />{approved ? 'Approved' : (item.vendor_approve_status || 'Pending')}</span></td>
          <td><button className="org-view-button" onClick={event => { event.stopPropagation(); onOpen(item); }}>View <ChevronRight size={14} /></button></td>
        </tr>;
      })}
    </tbody></table></div>
    <div className="org-pagination"><button disabled={safePage === 1} onClick={() => onPageChange(safePage - 1)}>Previous</button><span>Page <strong>{safePage}</strong> of <strong>{totalPages}</strong></span><button disabled={safePage === totalPages} onClick={() => onPageChange(safePage + 1)}>Next</button></div>
  </div>;
};

const AdminDashboard = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(null);
  const [showVendorTypeSelector, setShowVendorTypeSelector] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '' });
  const [editMode, setEditMode] = useState(false);
  const [editClinicId, setEditClinicId] = useState(null);
  const [editDoctorId, setEditDoctorId] = useState(null);
  const [editPharmacyId, setEditPharmacyId] = useState(null);
  const [editSupervisorId, setEditSupervisorId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkModalType, setBulkModalType] = useState('clinic');
  const [billingData, setBillingData] = useState({
    billing_model: 'subscription',
    subscription_amount: '',
    subscription_type: 'monthly',
    commission_rate: '',
    commission_type: 'percentage',
    billing_start_date: '',
    billing_end_date: '',
  });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [clinics, setClinics] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [labs, setLabs] = useState([]);
  const [homeCareProviders, setHomeCareProviders] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [organisationPage, setOrganisationPage] = useState(1);
  const [organisationPageSize, setOrganisationPageSize] = useState(10);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [planAssignment, setPlanAssignment] = useState({
    plan_id: '', business_model: 'cms_only', commercial_model: 'subscription_only', enabled_modules: [],
    subscription_amount: 0, currency: 'INR', commission_rate: 0, commission_type: 'percentage',
    billing_start_date: new Date().toISOString().slice(0, 10), billing_end_date: '', payment_status: 'pending', subscription_status: 'active',
  });

  const userRole = Cookies.get('role');

  useEffect(() => {
    setOrganisationPage(1);
  }, [activeSection, searchTerm, organisationPageSize]);

  useEffect(() => {
    AdminService.getSubscriptionPlans()
      .then((response) => setSubscriptionPlans(response?.status === 1 ? (response.data || []) : []))
      .catch((error) => console.error('Unable to load subscription plans:', error));
  }, [refreshKey]);

  const clinicsRef = useRef([]);
  const doctorsRef = useRef([]);
  const pharmaciesRef = useRef([]);
  const supervisorsRef = useRef([]);
  const specialitiesRef = useRef([]);

  const [formData, setFormData] = useState({
    clinic: {
      vendorTypeId: 3,
      vendorName: '',
      vendorEmail: '',
      vendorConNumber: '',
      location: '',
      regNumber: '',
      address: '',
      LicenceExpDate: '',
      vendor_profile_path: null,
      licencePath: null,
      authorizedSign: null,
      commercialLicence: null,
      civilIdPath: null,
      pharmacydataArray: [],
      doctorList: [],
      insuranceList: '',
      salesManRefCode: '',
      insurance_Yes_No: '',
      vendor_latlong: '',
      password: '',
    },
    doctor: {
      titleType: 'Dr.',
      vendorName: '',
      vendorEmail: '',
      vendorConNumber: '',
      specialty_id: '',
      education: '',
      experience: '',
      gender: '',
      regNumber: '',
      location: '',
      address: '',
      vendor_latlong: '',
      vendor_profile_path: null,
      license: null,
      authorizedSignature: null,
      commercialLicense: null,
      civilId: null,
      clinic_id: '',
      clinic_ids: [],
      conditions: [],
      procedures: [],
      is_committed: 0,
      commitment_type: 'normal',
      languages: '',
      password: '',
    },
    pharmacy: {
      vendorName: '',
      vendorEmail: '',
      vendorConNumber: '',
      location: '',
      regNumber: '',
      address: '',
      vendor_profile_path: null,
      license: null,
      authorizedSignature: null,
      commercialLicense: null,
      clinic_id: '',
      password: '',
    },
    lab: { vendorName: '', vendorEmail: '', password: '', vendorConNumber: '', location: '', regNumber: '', address: '', LicenceExpDate: '', vendor_latlong: '', vendor_profile_path: '' },
    homecare: { vendorName: '', vendorEmail: '', password: '', vendorConNumber: '', location: '', regNumber: '', address: '', LicenceExpDate: '', vendor_latlong: '', vendor_profile_path: '' },
    supervisor: {
      vendorName: '',
      role: 'supervisor',
      department: '',
      experience: '',
      clinic_id: '',
      vendorConNumber: '',
      vendorEmail: '',
      vendor_profile_path: null,
      password: '',
    },
  });

  const [accessModalVendor, setAccessModalVendor] = useState(null);

  const [openGroups, setOpenGroups] = useState({
    dashboard: true,
    leads: true,
    organisations: true,
    subscriptions: true,
    masters: true,
    admin: true,
  });

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const sidebarGroups = [
    {
      id: 'dashboard',
      title: 'Dashboard & Overview',
      items: [
        { id: 'overview', label: 'Executive Overview', icon: LayoutDashboard },
        { id: 'analytics', label: 'Platform Analytics', icon: BarChart2 },
        { id: 'revenue', label: 'Revenue & Settlements', icon: TrendingUp },
      ],
    },
    {
      id: 'leads',
      title: 'Patient Leads Engine',
      items: [
        { id: 'leads-all', label: 'Patient Leads Hub', icon: Inbox },
        { id: 'delivery-logs', label: 'Delivery & Retry Logs', icon: Send },
        { id: 'api-integrations', label: 'Vendor API Integrations', icon: SlidersHorizontal },
      ],
    },
    {
      id: 'organisations',
      title: 'Healthcare Organisations',
      items: [
        { id: 'all-vendors', label: 'All Vendors & Access Models', icon: Building2 },
        { id: 'clinic', label: 'Clinics CMS', icon: Building2, count: clinics.length },
        { id: 'doctor', label: 'Doctors CMS', icon: Users, count: doctors.length },
        { id: 'pharmacy', label: 'Pharmacies CMS', icon: Pill, count: pharmacies.length },
        { id: 'lab', label: 'Diagnostic Labs CMS', icon: FlaskConical, count: labs.length },
        { id: 'homecare', label: 'Home Care CMS', icon: HeartPulse, count: homeCareProviders.length },
        { id: 'pending-registrations', label: 'Pending Approvals', icon: UserPlus },
        { id: 'supervisor', label: 'Supervisors & Staff', icon: UserCheck, count: supervisors.length },
      ],
    },
    {
      id: 'subscriptions',
      title: 'Subscriptions & Monetisation',
      items: [
        { id: 'subscription-plans', label: 'Subscription Plans & Pricing', icon: CreditCard },
        { id: 'module-subscriptions', label: 'Vendor Module Allocations', icon: Layers },
      ],
    },
    {
      id: 'masters',
      title: 'Platform Masters',
      items: [
        { id: 'locations', label: 'Location Master', icon: MapPin },
        { id: 'currencies', label: 'Currency Master', icon: Globe },
        { id: 'speciality', label: 'Medical Specialties', icon: Award },
        { id: 'pharmacy-category', label: 'Pharmacy Categories', icon: Pill },
        { id: 'labtest-category', label: 'Lab Test Categories', icon: FlaskConical },
        { id: 'homecare-category', label: 'Home Care Categories', icon: HeartPulse },
      ],
    },
    {
      id: 'admin',
      title: 'Platform Administration',
      items: [
        { id: 'users', label: 'Super Admin Users', icon: Users2 },
        { id: 'roles', label: 'Roles & Permissions', icon: ShieldCheck },
        { id: 'settings', label: 'Platform Settings', icon: Settings2 },
      ],
    },
  ];

  // Fetch data on component mount
  useEffect(() => {
    const fetchClinics = async () => {
      try {
        const response = await ClinicService.getAllClinics();

        if (response.status !== 1) {
          throw new Error(response.msg || "Failed to fetch clinics");
        }

        const clinicData = Array.isArray(response.data)
          ? response.data.filter(
            (clinic) => clinic && typeof clinic === "object"
          )
          : [];

        clinicsRef.current = clinicData;
        setClinics(clinicData);
      } catch (error) {
        console.error("Error fetching clinics:", error.message);
        showCustomAlert(`Error fetching clinics: ${error.message}`);
      }
    };

    const fetchDoctors = async () => {
      try {
        const response = await DoctorService.getAllDoctors();
        if (Number(response.status) !== 1) {
          throw new Error(response.msg || "Failed to fetch doctors");
        }
        const mappedDoctors = Array.isArray(response.data)
          ? response.data
            .filter((doctor) => doctor && typeof doctor === 'object')
            .map((doctor) => ({
              id: doctor.id,
              titleType: doctor.titleType || doctor.title_type || 'Dr.',
              vendorName: doctor.vendor_name || doctor.vendorName || 'N/A',
              vendorEmail: doctor.vendor_email || doctor.vendorEmail || 'N/A',
              vendorConNumber: doctor.vendor_phone || doctor.vendorConNumber || 'N/A',
              specialty: doctor.speciality || 'N/A',
              specialty_id: doctor.speciality_id || 'N/A',
              education: doctor.vendor_contact_qualification || 'N/A',
              experience: doctor.experience || doctor.vendor_experience || '',
              gender: doctor.gender || doctor.vendor_contact_gender || '',
              regNumber: doctor.regNumber || doctor.vendor_regNumber || doctor.reg_no || '',
              location: doctor.location || doctor.vendor_location || '',
              address: doctor.address || doctor.vendor_address || '',
              vendor_latlong: doctor.vendor_latlong || doctor.lat_long || '',
              clinic: doctor.clinic_vendor_name || '',
              clinic_id: doctor.clinic_vendor_id || '',
              clinic_ids: Array.isArray(doctor.clinic_ids) ? doctor.clinic_ids : (doctor.clinic_vendor_id ? [doctor.clinic_vendor_id] : []),
              clinic_list: Array.isArray(doctor.clinic_list) ? doctor.clinic_list : [],
              vendor_profile_path: doctor.vendor_profile_path || null,
              license: doctor.license || doctor.licencePath || null,
              authorizedSignature: doctor.authorizedSignature || doctor.authorizedSign || null,
              commercialLicense: doctor.commercialLicense || doctor.commercialLicence || null,
              civilId: doctor.civil_id || doctor.civilIdPath || null,
              conditions: Array.isArray(doctor.conditions) ? doctor.conditions : [],
              procedures: Array.isArray(doctor.procedures) ? doctor.procedures : [],
              is_committed: doctor.is_committed === 1 || doctor.is_committed === '1' || doctor.commitment_type === 'committed' ? 1 : 0,
              commitment_type: doctor.commitment_type || (doctor.is_committed === 1 ? 'committed' : 'normal'),
              vendor_approve_status: doctor.vendor_approve_status || 'N/A',
            }))
          : [];
        doctorsRef.current = mappedDoctors;
        setDoctors(mappedDoctors);
      } catch (error) {
        showCustomAlert(`Error fetching doctors: ${error.message}`);
      }
    };

    const fetchPharmacies = async () => {
      try {
        const response = await PharmacyService.getAllPharmacy();
        if (Number(response.status) !== 1) {
          throw new Error(response.msg || "Failed to fetch pharmacies");
        }
        const mappedPharmacies = Array.isArray(response.data)
          ? response.data
            .filter((pharmacy) => pharmacy && typeof pharmacy === 'object')
            .map((pharmacy) => ({
              id: pharmacy.id,
              vendorName: pharmacy.vendor_name || pharmacy.vendorName || 'N/A',
              vendorEmail: pharmacy.vendor_email || pharmacy.vendorEmail || 'N/A',
              vendorConNumber: pharmacy.vendor_phone || pharmacy.vendorConNumber || 'N/A',
              address: pharmacy.vendor_address || 'N/A',
              location: pharmacy.vendor_location || 'N/A',
              regNumber: pharmacy.reg_no || 'N/A',
              clinic: pharmacy.clinic_vendor_name || '',
              clinic_id: pharmacy.clinic_vendor_id || '',
              vendor_profile_path: pharmacy.vendor_profile_path || null,
              vendor_approve_status: pharmacy.vendor_approve_status || 'N/A',
            }))
          : [];
        pharmaciesRef.current = mappedPharmacies;
        setPharmacies(mappedPharmacies);
      } catch (error) {
        showCustomAlert(`Error fetching pharmacies: ${error.message}`);
      }
    };

    const fetchSupervisors = async () => {
      try {
        const response = await SupervisorService.getAllSupervisor();
        if (Number(response.status) !== 1) {
          throw new Error(response.msg || "Failed to fetch supervisors");
        }
        const mappedSupervisors = Array.isArray(response.data)
          ? response.data
            .filter((supervisor) => supervisor && typeof supervisor === 'object')
            .map((supervisor) => ({
              id: supervisor.id,
              vendorName: supervisor.vendor_name || supervisor.vendorName || 'N/A',
              vendorEmail: supervisor.vendor_email || supervisor.vendorEmail || 'N/A',
              vendorConNumber: supervisor.vendor_phone || supervisor.vendorConNumber || 'N/A',
              clinic: supervisor.clinic_vendor_name || '',
              experience: supervisor.vendor_experience || 'N/A',
              department: supervisor.vendor_department || 'N/A',
              role: supervisor.vendor_role || 'N/A',
              clinic_id: supervisor.clinic_vendor_id || '',
              vendor_profile_path: supervisor.vendor_profile_path || null,
              vendor_approve_status: supervisor.vendor_approve_status || 'N/A',
            }))
          : [];
        supervisorsRef.current = mappedSupervisors;
        setSupervisors(mappedSupervisors);
      } catch (error) {
        showCustomAlert(`Error fetching supervisors: ${error.message}`);
      }
    };

    const fetchServiceVendors = async (vendorTypeId, setter) => {
      try {
        const response = await AdminService.getServiceVendors(vendorTypeId);
        if (Number(response.status) !== 1) throw new Error(response.msg || 'Failed to fetch service vendors');
        setter((response.data || []).map((item) => ({
          ...item,
          vendorName: item.vendor_name,
          vendorEmail: item.vendor_email,
          vendorConNumber: item.vendor_phone,
          location: item.vendor_location,
          address: item.vendor_address,
          regNumber: item.reg_no,
        })));
      } catch (error) {
        console.error('Error fetching service vendors:', error.message);
      }
    };

    const fetchSpecialities = async () => {
      try {
        const response = await DoctorService.getSpecialityList();
        if (response.msg !== 'Success') {
          throw new Error('Failed to fetch specialities');
        }
        specialitiesRef.current = Array.isArray(response.data)
          ? response.data
            .filter((speciality) => speciality && typeof speciality === 'object')
            .map((speciality) => ({
              id: speciality.specialityId,
              speciality: speciality.speciality || speciality.vendorName || 'N/A',
            }))
          : [];
      } catch (error) {
        showCustomAlert(`Error fetching specialities: ${error.message}`);
      }
    };

    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchClinics(),
          fetchDoctors(),
          fetchPharmacies(),
          fetchServiceVendors(2, setLabs),
          fetchServiceVendors(15, setHomeCareProviders),
          fetchSupervisors(),
          fetchSpecialities(),
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, [refreshKey]);

  const filteredData = () => {
    const term = searchTerm.toLowerCase();
    let filtered = [];
    switch (activeSection) {
      case 'clinic':
        filtered = clinics.filter(
          (item) =>
            (item?.vendor_name || '').toLowerCase().includes(term) ||
            (item?.vendor_address || '').toLowerCase().includes(term)
        );
        break;
      case 'doctor':
        filtered = doctors.filter(
          (item) =>
            (item?.vendorName || '').toLowerCase().includes(term) ||
            (item?.specialty || '').toLowerCase().includes(term) ||
            (item?.education || '').toLowerCase().includes(term)
        );
        break;
      case 'pharmacy':
        filtered = pharmacies.filter(
          (item) =>
            (item?.vendorName || '').toLowerCase().includes(term) ||
            (item?.address || '').toLowerCase().includes(term)
        );
        break;
      case 'lab':
        filtered = labs.filter((item) => (item.vendorName || '').toLowerCase().includes(term) || (item.address || '').toLowerCase().includes(term));
        break;
      case 'homecare':
        filtered = homeCareProviders.filter((item) => (item.vendorName || '').toLowerCase().includes(term) || (item.address || '').toLowerCase().includes(term));
        break;
      case 'supervisor':
        filtered = supervisors.filter(
          (item) =>
            (item?.vendorName || '').toLowerCase().includes(term) ||
            (item?.role || '').toLowerCase().includes(term)
        );
        break;
      case 'all-vendors':
        const combinedVendors = [
          ...clinics.map(c => ({ ...c, vendorTypeLabel: 'Clinic' })),
          ...doctors.map(d => ({ ...d, vendorTypeLabel: 'Doctor' })),
          ...pharmacies.map(p => ({ ...p, vendorTypeLabel: 'Pharmacy' })),
          ...labs.map(v => ({ ...v, vendorTypeLabel: 'Diagnostic Lab' })),
          ...homeCareProviders.map(v => ({ ...v, vendorTypeLabel: 'Home Care' })),
          ...supervisors.map(s => ({ ...s, vendorTypeLabel: 'Supervisor' }))
        ];
        filtered = combinedVendors.filter(
          (item) =>
            (item?.vendor_name || item?.vendorName || '').toLowerCase().includes(term) ||
            (item?.vendor_email || item?.vendorEmail || '').toLowerCase().includes(term) ||
            (item?.vendor_phone || item?.vendorConNumber || '').toLowerCase().includes(term) ||
            (item?.vendorTypeLabel || '').toLowerCase().includes(term)
        );
        break;
      case 'pending-registrations':
        const allPending = [
          ...clinics.filter(c => c.vendor_approve_status !== 'A').map(c => ({ ...c, vendorTypeLabel: 'Clinic' })),
          ...doctors.filter(d => d.vendor_approve_status !== 'A').map(d => ({ ...d, vendorTypeLabel: 'Doctor' })),
          ...pharmacies.filter(p => p.vendor_approve_status !== 'A').map(p => ({ ...p, vendorTypeLabel: 'Pharmacy' }))
          , ...labs.filter(v => v.vendor_approve_status !== 'A').map(v => ({ ...v, vendorTypeLabel: 'Diagnostic Lab' }))
          , ...homeCareProviders.filter(v => v.vendor_approve_status !== 'A').map(v => ({ ...v, vendorTypeLabel: 'Home Care' }))
        ];
        filtered = allPending.filter(
          (item) =>
            (item?.vendor_name || item?.vendorName || '').toLowerCase().includes(term) ||
            (item?.vendor_email || item?.vendorEmail || '').toLowerCase().includes(term) ||
            (item?.vendorTypeLabel || '').toLowerCase().includes(term)
        );
        break;
      default:
        filtered = [];
    }
    return filtered;
  };

  const getLinkedDoctors = (clinicId) => {
    return doctorsRef.current.filter((doctor) => {
      if (Array.isArray(doctor.clinic_ids) && doctor.clinic_ids.length > 0) {
        return doctor.clinic_ids.map(Number).includes(Number(clinicId));
      }
      return Number(doctor.clinic_id) === Number(clinicId);
    });
  };

  const getLinkedPharmacies = (clinicId) => {
    return pharmaciesRef.current.filter((pharmacy) => pharmacy.clinic_id === clinicId);
  };

  const handleBillingChange = (field, value) => {
    setBillingData(prev => ({ ...prev, [field]: value }));
  };

  const handleFormChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
  };

  const handleFileChange = (section, field, event) => {
    const file = event.target.files[0];
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: file ? file : null },
    }));
  };

  const showCustomAlert = (message) => {
    setAlert({ show: true, message });
    setTimeout(() => {
      setAlert({ show: false, message: '' });
    }, 3000);
  };

  const validateClinicForm = () => {
    const { vendorName, vendorEmail, vendorConNumber, location, regNumber, address, LicenceExpDate, vendor_profile_path } = formData.clinic;
    return (
      vendorName &&
      vendorEmail &&
      vendorConNumber &&
      location &&
      regNumber &&
      address &&
      LicenceExpDate &&
      (editMode || vendor_profile_path)
    );
  };

  const validateDoctorForm = () => {
    const { vendorName, vendorEmail, vendorConNumber, specialty_id, education, vendor_profile_path, clinic_id, clinic_ids } = formData.doctor;
    const hasClinic = (Array.isArray(clinic_ids) && clinic_ids.length > 0) || Boolean(clinic_id);
    return (
      vendorName &&
      vendorEmail &&
      vendorConNumber &&
      specialty_id &&
      education &&
      (editMode || vendor_profile_path) &&
      hasClinic
    );
  };

  const validatePharmacyForm = () => {
    const { vendorName, vendorEmail, vendorConNumber, location, regNumber, address, vendor_profile_path, clinic_id } = formData.pharmacy;
    return (
      vendorName &&
      vendorEmail &&
      vendorConNumber &&
      location &&
      regNumber &&
      address &&
      (editMode || vendor_profile_path) &&
      clinic_id
    );
  };

  const validateSupervisorForm = () => {
    const { vendorName, role, department, experience, clinic_id, vendorConNumber, vendorEmail, password, vendor_profile_path } = formData.supervisor;
    if (editMode) {
      return vendorName && role && department && experience && clinic_id && vendorConNumber && vendorEmail;
    } else {
      return vendorName && role && department && experience && clinic_id && vendorConNumber && vendorEmail && password && vendor_profile_path;
    }
  };

  const handleCreateNew = () => {
    setEditMode(false);
    setEditClinicId(null);
    setEditDoctorId(null);
    setEditPharmacyId(null);
    setEditSupervisorId(null);
    setPlanAssignment({
      plan_id: '', business_model: 'cms_only', commercial_model: 'subscription_only', enabled_modules: [],
      subscription_amount: 0, currency: 'INR', commission_rate: 0, commission_type: 'percentage',
      billing_start_date: new Date().toISOString().slice(0, 10), billing_end_date: '', payment_status: 'pending', subscription_status: 'active',
    });
    if (activeSection === 'doctor') {
      setFormData((prev) => ({
        ...prev,
        doctor: {
          titleType: 'Dr.',
          vendorName: '',
          vendorEmail: '',
          vendorConNumber: '',
          specialty_id: '',
          education: '',
          experience: '',
          gender: '',
          regNumber: '',
          location: '',
          address: '',
          vendor_profile_path: null,
          license: null,
          authorizedSignature: null,
          commercialLicense: null,
          civilId: null,
          clinic_id: '',
          clinic_ids: [],
          languages: '',
          password: '',
        },
      }));
    }
    if (activeSection === 'all-vendors') {
      setShowVendorTypeSelector(true);
    } else if (activeSection === 'pending-registrations') {
      setShowForm('clinic');
    } else {
      setShowForm(activeSection);
    }
  };

  const vendorCreationModules = [
    { id: 'clinic', label: 'Clinics CMS', description: 'Create and configure a clinic organisation.', icon: Building2 },
    { id: 'doctor', label: 'Doctors CMS', description: 'Add a doctor and associate clinics and specialties.', icon: Stethoscope },
    { id: 'pharmacy', label: 'Pharmacies CMS', description: 'Register an independent or clinic-linked pharmacy.', icon: Pill },
    { id: 'lab', label: 'Diagnostic Labs CMS', description: 'Register and manage a diagnostic laboratory.', icon: FlaskConical },
    { id: 'homecare', label: 'Home Care CMS', description: 'Register and manage a home-care service provider.', icon: HeartPulse },
  ];

  const selectVendorCreationModule = (moduleId) => {
    setShowVendorTypeSelector(false);
    setActiveSection(moduleId);
    setSearchTerm('');
    if (['clinic', 'doctor', 'pharmacy', 'lab', 'homecare'].includes(moduleId)) {
      setShowForm(moduleId);
    }
  };

  const handlePlanAssignmentChange = (next) => {
    setPlanAssignment(next);
    const cycle = subscriptionPlans.find((plan) => String(plan.id) === String(next.plan_id))?.billing_cycle || 'monthly';
    setBillingData({
      billing_model: next.commercial_model === 'commission_only' ? 'commission' : 'subscription',
      subscription_amount: next.subscription_amount || 0,
      subscription_type: cycle === 'yearly' ? 'yearly' : 'monthly',
      commission_rate: next.commission_rate || 0,
      commission_type: next.commission_type || 'percentage',
      billing_start_date: next.billing_start_date || '',
      billing_end_date: next.billing_end_date || '',
    });
  };

  const savePlanAssignment = async (vendorId) => {
    if (!vendorId || !planAssignment.plan_id) throw new Error('A subscription plan must be selected.');
    const existingResponse = await AdminService.getVendorModuleSubscriptions(vendorId);
    const existing = existingResponse?.status === 1 && Array.isArray(existingResponse.data) ? existingResponse.data[0] : null;
    const modules = Array.isArray(planAssignment.enabled_modules) ? planAssignment.enabled_modules : [];
    const hasLead = modules.includes('lead_generation');
    const hasCms = modules.some((module) => module !== 'lead_generation');
    const businessModel = hasLead && hasCms ? 'hybrid' : hasLead ? 'leads_only' : 'cms_only';
    const response = await AdminService.saveVendorModuleSubscription({
      ...planAssignment,
      id: existing?.id || null,
      business_model: businessModel,
      vendor_id: Number(vendorId),
      plan_id: Number(planAssignment.plan_id),
      subscription_amount: Number(planAssignment.subscription_amount || 0),
      commission_rate: Number(planAssignment.commission_rate || 0),
      billing_start_date: String(planAssignment.billing_start_date || '').slice(0, 10),
      billing_end_date: String(planAssignment.billing_end_date || '').slice(0, 10),
    });
    if (Number(response?.status) !== 1) throw new Error(response?.msg || 'Unable to allocate subscription plan.');
  };

  const loadPlanAssignment = async (vendorId) => {
    try {
      const response = await AdminService.getVendorModuleSubscriptions(vendorId);
      const current = response?.status === 1 && Array.isArray(response.data) ? response.data[0] : null;
      if (!current) return;
      let modules = current.enabled_modules || [];
      if (typeof modules === 'string') {
        try { modules = JSON.parse(modules); } catch (_error) { modules = []; }
      }
      handlePlanAssignmentChange({ ...current, enabled_modules: modules, plan_id: current.plan_id || '' });
    } catch (error) {
      console.error('Unable to load vendor plan allocation:', error);
    }
  };

  const handleFormSubmit = async (section) => {
    const admin_id = Cookies.get('adminid');
    if (['clinic', 'doctor', 'pharmacy', 'lab', 'homecare'].includes(section) && !planAssignment.plan_id) {
      showCustomAlert('Please select a Subscription Plan & Pricing plan before creating the vendor.');
      return;
    }
    if (section === 'clinic') {
      if (!validateClinicForm()) {
        showCustomAlert('Please fill all mandatory fields.');
        return;
      }
      const clinicData = {
        vendorTypeId: 3,
        id: formData.clinic.id || null,
        vendorName: formData.clinic.vendorName,
        vendorConNumber: formData.clinic.vendorConNumber,
        vendorEmail: formData.clinic.vendorEmail,
        regNumber: formData.clinic.regNumber,
        taxnumber: formData.clinic.taxnumber,
        LicenceExpDate: formData.clinic.LicenceExpDate,
        address: formData.clinic.address,
        location: formData.clinic.location,
        doctorList: JSON.stringify(formData.clinic.doctorList || []),
        insuranceList: formData.clinic.insuranceList || '',
        salesManRefCode: formData.clinic.salesManRefCode || '',
        insurance_Yes_No: formData.clinic.insurance_Yes_No || '',
        vendor_latlong: formData.clinic.vendor_latlong || '',
        pharmacydataArray: JSON.stringify(formData.clinic.pharmacydataArray || []),
        adminId: admin_id,
        password: formData.clinic.password || '',
      };
      const files = {
        profilePhoto: formData.clinic.vendor_profile_path,
        license: formData.clinic.licencePath,
        authorizedSignature: formData.clinic.authorizedSign,
        commercialLicense: formData.clinic.commercialLicence,
      };
      try {
        let result;
        if (editMode) {
          result = await ClinicService.updateClinic(editClinicId, clinicData, files);
          if (result.status === 1) {
            showCustomAlert(result.msg);
            const updatedClinic = {
              admin_id: result.data.admin_id,
              id: result.data.id,
              vendor_name: result.data.vendor_name,
              vendor_email: result.data.vendor_email,
              vendor_phone: result.data.vendor_contact_mobile,
              vendor_profile_path: result.data.vendor_profile_path,
              vendor_approve_status: result.data.vendor_approve_status,
              vendor_address: result.data.vendor_address,
              vendor_location: result.data.vendor_location,
              vendor_type_id: result.data.vendor_type_id,
              reg_no: result.data.reg_no,
              exp_date: result.data.exp_date,
            };
            clinicsRef.current = clinicsRef.current.map((clinic) =>
              clinic.id === editClinicId ? updatedClinic : clinic
            );
            setClinics(clinicsRef.current);
            setSelectedClinic(null);
          } else {
            showCustomAlert(result.msg);
            return;
          }
        } else {
          result = await ClinicService.addClinic(clinicData, files);
          if (result.status === 1) {
            showCustomAlert(result.msg);
            const addClinic = {
              admin_id: result.data?.admin_id,
              id: result.clinic_id || result.data?.id,
              vendor_name: result.data?.vendor_name,
              vendor_email: result.data?.vendor_email,
              vendor_phone: result.data?.vendor_contact_mobile,
              vendor_profile_path: result.data?.vendor_profile_path,
              vendor_approve_status: result.data?.vendor_approve_status || 'A',
              vendor_address: result.data?.vendor_address,
              vendor_location: result.data?.vendor_location,
              vendor_type_id: result.data?.vendor_type_id || 3,
              reg_no: result.data?.reg_no,
              exp_date: result.data?.exp_date,
              billing_model: billingData.billing_model,
              subscription_amount: billingData.subscription_amount,
              subscription_type: billingData.subscription_type,
              commission_rate: billingData.commission_rate,
              commission_type: billingData.commission_type,
              billing_start_date: billingData.billing_start_date,
              billing_end_date: billingData.billing_end_date,
            };
            clinicsRef.current = [...clinicsRef.current, addClinic];
            setClinics(clinicsRef.current);
          } else {
            showCustomAlert(result.msg);
            return;
          }
        }

        // Save billing settings for this clinic
        const savedClinicId = editMode ? editClinicId : (result.clinic_id || result.data?.id);
        if (savedClinicId) {
          try {
            await savePlanAssignment(savedClinicId);
            await apiFetch(`${API_BASE_URL}/clinic-billing/${savedClinicId}`, {
              method: 'PUT',
              body: JSON.stringify(billingData),
            });
          } catch (billingErr) {
            showCustomAlert(`Clinic saved, but plan allocation failed: ${billingErr.message}`);
            return;
          }
        }

        setBillingData({
          billing_model: 'subscription',
          subscription_amount: '',
          subscription_type: 'monthly',
          commission_rate: '',
          commission_type: 'percentage',
          billing_start_date: '',
          billing_end_date: '',
        });
        setRefreshKey((prev) => prev + 1);
      } catch (error) {
        showCustomAlert(`Error: ${error.message}`);
        return;
      }
    } else if (section === 'doctor') {
      if (!validateDoctorForm()) {
        showCustomAlert('Please fill all mandatory fields and select at least one clinic.');
        return;
      }

      const selectedClinicIds = Array.isArray(formData.doctor.clinic_ids) && formData.doctor.clinic_ids.length > 0
        ? formData.doctor.clinic_ids.map(Number).filter(Boolean)
        : formData.doctor.clinic_id
          ? [parseInt(formData.doctor.clinic_id)].filter(Boolean)
          : [];

      const primaryClinicId = selectedClinicIds[0] || (formData.doctor.clinic_id ? parseInt(formData.doctor.clinic_id) : 0);

      const doctorData = {
        titleType: formData.doctor.titleType || 'Dr.',
        vendorName: formData.doctor.vendorName,
        vendorEmail: formData.doctor.vendorEmail,
        vendorConNumber: formData.doctor.vendorConNumber,
        specialty: formData.doctor.specialty_id,
        vendor_contact_qualification: formData.doctor.education,
        experience: formData.doctor.experience || '',
        vendor_experience: formData.doctor.experience || '',
        gender: formData.doctor.gender || '',
        vendor_regNumber: formData.doctor.regNumber || '',
        vendor_address: formData.doctor.address || '',
        location: formData.doctor.location || '',
        vendor_latlong: formData.doctor.vendor_latlong || '',
        clinic_id: primaryClinicId,
        clinic_ids: selectedClinicIds,
        conditions: formData.doctor.conditions || [],
        procedures: formData.doctor.procedures || [],
        is_committed: formData.doctor.is_committed || (formData.doctor.commitment_type === 'committed' ? 1 : 0),
        commitment_type: formData.doctor.commitment_type || (formData.doctor.is_committed === 1 ? 'committed' : 'normal'),
        languages: formData.doctor.languages || '',
        admin_id: admin_id,
        password: formData.doctor.password || '',
      };
      const files = {
        profilePhoto: formData.doctor.vendor_profile_path,
        license: formData.doctor.license,
        authorizedSignature: formData.doctor.authorizedSignature,
        commercialLicense: formData.doctor.commercialLicense,
        civilId: formData.doctor.civilId,
      };
      try {
        let result;
        if (editMode) {
          result = await DoctorService.updateDoctor(editDoctorId, doctorData, files);
          if (!result || result.status !== 1) {
            showCustomAlert(result?.msg || 'Failed to update doctor');
            return;
          }
          showCustomAlert('Doctor updated successfully!');
          const doctorPayload = result.data || {};
          const assignedClinicNames = selectedClinicIds
            .map((cId) => clinicsRef.current.find((c) => c.id === cId)?.vendor_name || `Clinic ${cId}`)
            .join(', ');

          const updatedDoctor = {
            id: editDoctorId,
            titleType: doctorPayload.titleType || doctorPayload.title_type || doctorData.titleType || 'Dr.',
            vendorName: doctorPayload.vendorName || doctorPayload.vendor_name || doctorData.vendorName,
            vendorEmail: doctorPayload.vendorEmail || doctorPayload.vendor_email || doctorData.vendorEmail,
            vendorConNumber: doctorPayload.vendorConNumber || doctorPayload.vendor_contact_mobile || doctorPayload.vendor_phone || doctorData.vendorConNumber,
            specialty: specialitiesRef.current.find((s) => s.id === parseInt(doctorPayload.specialty_id || doctorPayload.doctor_speciality_id || doctorData.specialty))?.speciality || 'N/A',
            specialty_id: doctorPayload.specialty_id || doctorPayload.doctor_speciality_id || doctorData.specialty,
            education: doctorPayload.education || doctorPayload.vendor_contact_qualification || doctorData.vendor_contact_qualification,
            experience: doctorPayload.experience || doctorPayload.vendor_experience || doctorData.experience || '',
            gender: doctorPayload.gender || doctorPayload.vendor_contact_gender || doctorData.gender || '',
            regNumber: doctorPayload.regNumber || doctorPayload.vendor_regNumber || doctorPayload.reg_no || doctorData.vendor_regNumber || '',
            location: doctorPayload.location || doctorPayload.vendor_location || doctorData.location || '',
            address: doctorPayload.address || doctorPayload.vendor_address || doctorData.vendor_address || '',
            vendor_latlong: doctorPayload.vendor_latlong || doctorData.vendor_latlong || '',
            clinic_id: primaryClinicId,
            clinic_ids: selectedClinicIds,
            clinic: assignedClinicNames || doctorPayload.clinic_name || 'N/A',
            clinic_list: selectedClinicIds.map((cId) => ({
              clinicId: cId,
              clinic: clinicsRef.current.find((c) => c.id === cId)?.vendor_name || `Clinic ${cId}`,
            })),
            conditions: formData.doctor.conditions || doctorPayload.conditions || [],
            procedures: formData.doctor.procedures || doctorPayload.procedures || [],
            is_committed: doctorData.is_committed,
            commitment_type: doctorData.commitment_type,
            languages: doctorPayload.languages || doctorData.languages || '',
            vendor_profile_path: doctorPayload.vendor_profile_path || files.profilePhoto || null,
            license: doctorPayload.license || doctorPayload.licencePath || files.license || null,
            authorizedSignature: doctorPayload.authorizedSignature || doctorPayload.authorizedSign || files.authorizedSignature || null,
            commercialLicense: doctorPayload.commercialLicense || doctorPayload.commercialLicence || files.commercialLicense || null,
            civilId: doctorPayload.civilId || doctorPayload.civilIdPath || files.civilId || null,
            vendor_approve_status: doctorPayload.vendor_approve_status || 'A',
          };
          const newDoctorList = doctorsRef.current.map((doctor) =>
            doctor.id === editDoctorId ? updatedDoctor : doctor
          );
          doctorsRef.current = newDoctorList;
          setDoctors([...newDoctorList]);
          if (selectedDoctor && selectedDoctor.id === editDoctorId) {
            setSelectedDoctor(updatedDoctor);
          }
          setSelectedDoctor(null);
        } else {
          result = await DoctorService.addDoctor(doctorData, files);
          if (result && (result.status === 1 || result.status === '1' || result.doctor_id || result.data?.id)) {
            showCustomAlert(result.msg || 'Doctor registered successfully!');
            const doctorPayload = result.data || {};
            const assignedClinicNames = selectedClinicIds
              .map((cId) => clinicsRef.current.find((c) => c.id === cId)?.vendor_name || `Clinic ${cId}`)
              .join(', ');

            const addDoctor = {
              id: result.doctor_id || doctorPayload.id,
              titleType: doctorPayload.titleType || doctorPayload.title_type || doctorData.titleType || 'Dr.',
              vendorName: doctorPayload.vendor_name || doctorPayload.vendorName || doctorData.vendorName,
              vendorEmail: doctorPayload.vendor_email || doctorPayload.vendorEmail || doctorData.vendorEmail,
              vendorConNumber: doctorPayload.vendor_contact_mobile || doctorPayload.vendor_phone || doctorPayload.vendorConNumber || doctorData.vendorConNumber,
              specialty: specialitiesRef.current.find((s) => s.id === parseInt(doctorPayload.doctor_speciality_id || doctorPayload.specialty_id || doctorData.specialty))?.speciality || 'N/A',
              specialty_id: doctorPayload.doctor_speciality_id || doctorPayload.specialty_id || doctorData.specialty,
              education: doctorPayload.vendor_contact_qualification || doctorPayload.education || doctorData.vendor_contact_qualification,
              experience: doctorPayload.experience || doctorPayload.vendor_experience || doctorData.experience || '',
              gender: doctorPayload.gender || doctorPayload.vendor_contact_gender || doctorData.gender || '',
              regNumber: doctorPayload.regNumber || doctorPayload.vendor_regNumber || doctorPayload.reg_no || doctorData.vendor_regNumber || '',
              location: doctorPayload.location || doctorPayload.vendor_location || doctorData.location || '',
              address: doctorPayload.address || doctorPayload.vendor_address || doctorData.vendor_address || '',
              vendor_latlong: doctorPayload.vendor_latlong || doctorData.vendor_latlong || '',
              clinic_id: primaryClinicId,
              clinic_ids: selectedClinicIds,
              clinic: assignedClinicNames || 'N/A',
              clinic_list: selectedClinicIds.map((cId) => ({
                clinicId: cId,
                clinic: clinicsRef.current.find((c) => c.id === cId)?.vendor_name || `Clinic ${cId}`,
              })),
              conditions: formData.doctor.conditions || doctorPayload.conditions || [],
              procedures: formData.doctor.procedures || doctorPayload.procedures || [],
              is_committed: doctorData.is_committed,
              commitment_type: doctorData.commitment_type,
              vendor_profile_path: doctorPayload.vendor_profile_path || files.profilePhoto || null,
              license: doctorPayload.license || doctorPayload.licencePath || files.license || null,
              authorizedSignature: doctorPayload.authorizedSignature || doctorPayload.authorizedSign || files.authorizedSignature || null,
              commercialLicense: doctorPayload.commercialLicense || doctorPayload.commercialLicence || files.commercialLicense || null,
              civilId: doctorPayload.civilId || doctorPayload.civilIdPath || files.civilId || null,
              vendor_approve_status: doctorPayload.vendor_approve_status || 'A',
            };
            doctorsRef.current = [...doctorsRef.current, addDoctor];
            setDoctors([...doctorsRef.current]);
          } else {
            showCustomAlert(result?.msg || 'Failed to register doctor');
          }
        }
        const savedDoctorId = editMode ? editDoctorId : (result?.doctor_id || result?.data?.id);
        await savePlanAssignment(savedDoctorId);
        setRefreshKey((prev) => prev + 1);
      } catch (error) {
        showCustomAlert(`Error: ${error.message}`);
        return;
      }
    } else if (section === 'pharmacy') {
      if (!validatePharmacyForm()) {
        showCustomAlert('Please fill all mandatory fields.');
        return;
      }
      const pharmacyData = {
        vendorName: formData.pharmacy.vendorName,
        vendorEmail: formData.pharmacy.vendorEmail,
        vendorConNumber: formData.pharmacy.vendorConNumber,
        location: formData.pharmacy.location,
        regNumber: formData.pharmacy.regNumber,
        address: formData.pharmacy.address,
        clinic_id: parseInt(formData.pharmacy.clinic_id),
        adminId: admin_id,
        password: formData.pharmacy.password || '',
      };
      const files = {
        profilePhoto: formData.pharmacy.vendor_profile_path,
        license: formData.pharmacy.license,
        authorizedSignature: formData.pharmacy.authorizedSignature,
        commercialLicense: formData.pharmacy.commercialLicense,
      };
      try {
        let result;
        if (editMode) {
          result = await PharmacyService.updatePharmacy(editPharmacyId, pharmacyData, files);
          showCustomAlert('Pharmacy updated successfully!');
          const resData = result?.data || result || {};
          const updatedPharmacy = {
            id: resData.id || editPharmacyId,
            vendorName: resData.vendor_name || pharmacyData.vendorName,
            vendorEmail: resData.vendor_email || pharmacyData.vendorEmail,
            vendorConNumber: resData.vendor_contact_mobile || pharmacyData.vendorConNumber,
            location: resData.vendor_location || pharmacyData.location,
            address: resData.vendor_address || pharmacyData.address,
            reg_no: resData.reg_no || pharmacyData.regNumber,
            clinic_id: resData.clinic_id || pharmacyData.clinic_id,
            clinic: clinicsRef.current.find((c) => c.id === parseInt(resData.clinic_id || pharmacyData.clinic_id))?.vendor_name || resData.clinic,
            vendor_profile_path: resData.vendor_profile_path || files.profilePhoto,
            vendor_approve_status: resData.vendor_approve_status,
          };
          const newPharmacyList = pharmaciesRef.current.map((pharmacy) =>
            pharmacy.id === editPharmacyId ? updatedPharmacy : pharmacy
          );
          pharmaciesRef.current = newPharmacyList;
          setPharmacies([...newPharmacyList]);
          setSelectedPharmacy(null);
        } else {
          result = await PharmacyService.addPharmacy(pharmacyData, files);
          if (result.status === 1) {
            showCustomAlert('Pharmacy registered successfully!');
            const addPharmacy = {
              id: result.data.id,
              vendorName: result.data.vendor_name,
              vendorEmail: result.data.vendor_email,
              vendorConNumber: result.data.vendor_contact_mobile,
              location: result.data.vendor_location,
              address: result.data.vendor_address,
              reg_no: result.data.reg_no,
              clinic_id: result.data.clinic_id,
              clinic: clinicsRef.current.find((c) => c.id === parseInt(result.data.clinic_id))?.vendor_name || 'N/A',
              vendor_profile_path: result.data.vendor_profile_path,
              vendor_approve_status: result.data.vendor_approve_status,
            };
            pharmaciesRef.current = [...pharmaciesRef.current, addPharmacy];
            setPharmacies([...pharmaciesRef.current]);
          } else {
            showCustomAlert(result.msg);
          }

        }
        const savedPharmacyId = editMode ? editPharmacyId : result?.data?.id;
        await savePlanAssignment(savedPharmacyId);
        setRefreshKey((prev) => prev + 1);
      } catch (error) {
        showCustomAlert(`Error: ${error.message}`);
        return;
      }
    } else if (section === 'lab' || section === 'homecare') {
      const data = formData[section];
      const required = [data.vendorName, data.vendorEmail, data.password, data.vendorConNumber, data.location, data.regNumber, data.address];
      if (required.some((value) => !String(value || '').trim())) {
        showCustomAlert('Please fill all mandatory fields.');
        return;
      }
      try {
        const result = await AdminService.createServiceVendor({
          vendor_type_id: section === 'lab' ? 2 : 15,
          vendor_name: data.vendorName,
          vendor_email: data.vendorEmail,
          vendor_phone: data.vendorConNumber,
          location: data.location,
          address: data.address,
          registration_number: data.regNumber,
          license_expiry: data.LicenceExpDate || null,
          vendor_latlong: data.vendor_latlong || '',
          profile_path: data.vendor_profile_path || '',
          password: data.password,
          admin_id,
        });
        if (Number(result.status) !== 1 || !result.data?.id) throw new Error(result.msg || 'Vendor creation failed');
        await savePlanAssignment(result.data.id);
        const mapped = { ...result.data, vendorName: result.data.vendor_name, vendorEmail: result.data.vendor_email, vendorConNumber: result.data.vendor_phone, location: result.data.vendor_location, address: result.data.vendor_address, regNumber: result.data.reg_no };
        if (section === 'lab') setLabs((current) => [mapped, ...current]);
        else setHomeCareProviders((current) => [mapped, ...current]);
        showCustomAlert(`${section === 'lab' ? 'Diagnostic Lab' : 'Home Care Provider'} created and subscription plan allocated successfully.`);
        setShowForm(null);
        setRefreshKey((previous) => previous + 1);
      } catch (error) {
        showCustomAlert(`Error: ${error.message}`);
      }
    } else if (section === 'supervisor') {
      if (!validateSupervisorForm()) {
        showCustomAlert('Please fill all mandatory fields.');
        return;
      }
      const supervisorData = {
        vendorTypeId: 14,
        vendorName: formData.supervisor.vendorName,
        vendorEmail: formData.supervisor.vendorEmail,
        vendorConNumber: formData.supervisor.vendorConNumber,
        role: formData.supervisor.role,
        department: formData.supervisor.department,
        experience: formData.supervisor.experience,
        clinic_id: parseInt(formData.supervisor.clinic_id),
        adminId: admin_id,
        password: formData.supervisor.password,
      };
      const files = {
        profilePhoto: formData.supervisor.vendor_profile_path,
      };
      try {
        let result;
        if (editMode) {
          result = await SupervisorService.updateSupervisor(editSupervisorId, supervisorData, files);
          showCustomAlert('Supervisor updated successfully!');
          const updatedSupervisor = {
            id: result.data.id,
            vendorName: result.data.vendor_name,
            vendorEmail: result.data.vendor_email,
            vendorConNumber: result.data.vendor_contact_mobile,
            role: result.data.vendor_role,
            department: result.data.vendor_department,
            experience: result.data.vendor_experience,
            clinic_id: result.data.clinic_id,
            clinic: clinicsRef.current.find((c) => c.id === parseInt(result.data.clinic_id))?.vendor_name || 'N/A',
            vendor_profile_path: result.data.vendor_profile_path,
            vendor_approve_status: result.data.vendor_approve_status,
            password: formData.supervisor.password,
          };
          const newSupervisorList = supervisorsRef.current.map((supervisor) =>
            supervisor.id === editSupervisorId ? updatedSupervisor : supervisor
          );
          supervisorsRef.current = newSupervisorList;
          setSupervisors([...newSupervisorList]);
          setSelectedSupervisor(null);
        } else {
          result = await SupervisorService.addSupervisor(supervisorData, files);
          showCustomAlert('Supervisor registered successfully!');
          const addSupervisor = {
            id: result.data.resObj.id,
            vendorName: result.data.resObj.vendor_name,
            vendorEmail: result.data.resObj.vendor_email,
            vendorConNumber: result.data.resObj.vendor_contact_mobile,
            role: result.data.resObj.vendor_role,
            department: result.data.resObj.vendor_department,
            experience: result.data.resObj.vendor_experience,
            clinic_id: result.data.resObj.clinic_id,
            clinic: clinicsRef.current.find((c) => c.id === parseInt(result.data.resObj.clinic_id))?.vendor_name || 'N/A',
            vendor_profile_path: result.data.resObj.vendor_profile_path,
            vendor_approve_status: result.data.resObj.vendor_approve_status,
            password: result.data.resObj.password,
          };
          supervisorsRef.current = [...supervisorsRef.current, addSupervisor];
          setSupervisors([...supervisorsRef.current]);
          setSelectedSupervisor(null);
        }
        setRefreshKey((prev) => prev + 1);
      } catch (error) {
        showCustomAlert(`Error: ${error.message}`);
        return;
      }
    }

    setFormData({
      clinic: {
        vendorTypeId: 3,
        vendorName: '',
        vendorEmail: '',
        vendorConNumber: '',
        location: '',
        regNumber: '',
        address: '',
        LicenceExpDate: '',
        vendor_profile_path: null,
        licencePath: null,
        authorizedSign: null,
        commercialLicence: null,
        civilIdPath: null,
        pharmacydataArray: [],
        doctorList: [],
        insuranceList: '',
        salesManRefCode: '',
        insurance_Yes_No: '',
        vendor_latlong: '',
      },
      doctor: {
        titleType: 'Dr.',
        vendorName: '',
        vendorEmail: '',
        vendorConNumber: '',
        specialty_id: '',
        education: '',
        experience: '',
        gender: '',
        regNumber: '',
        location: '',
        address: '',
        vendor_profile_path: null,
        license: null,
        authorizedSignature: null,
        commercialLicense: null,
        civilId: null,
        clinic_id: '',
        clinic_ids: [],
        conditions: [],
        procedures: [],
        is_committed: 0,
        commitment_type: 'normal',
        password: '',
      },
      pharmacy: {
        vendorName: '',
        vendorEmail: '',
        vendorConNumber: '',
        location: '',
        regNumber: '',
        address: '',
        vendor_profile_path: null,
        license: null,
        authorizedSignature: null,
        commercialLicense: null,
        clinic_id: '',
      },
      supervisor: {
        vendorName: '',
        vendorEmail: '',
        vendorConNumber: '',
        role: '',
        department: '',
        experience: '',
        clinic_id: '',
        password: '',
        vendor_profile_path: null,
      },
    });
    setShowForm(null);
    setEditMode(false);
    setEditClinicId(null);
    setEditDoctorId(null);
    setEditPharmacyId(null);
    setEditSupervisorId(null);
  };

  const handleEditClinic = async (clinic) => {
    setFormData({
      ...formData,
      clinic: {
        id: clinic.id,
        vendorTypeId: 3,
        vendorName: clinic.vendor_name || '',
        vendorEmail: clinic.vendor_email || '',
        vendorConNumber: clinic.vendor_phone || '',
        location: clinic.vendor_location || '',
        regNumber: clinic.reg_no || '',
        address: clinic.vendor_address || '',
        password: clinic.password || '',
        LicenceExpDate: clinic.exp_date ? clinic.exp_date.toString().slice(0, 10) : '',
        vendor_profile_path: clinic.vendor_profile_path || null,
        licencePath: clinic.vendor_profile_path,
        authorizedSign: clinic.vendor_profile_path,
        commercialLicence: clinic.vendor_profile_path,
        civilIdPath: null,
        pharmacydataArray: clinic.pharmacydataArray ? JSON.parse(clinic.pharmacydataArray) : [],
        doctorList: clinic.doctorList ? JSON.parse(clinic.doctorList) : [],
        insuranceList: clinic.insuranceList || '',
        salesManRefCode: clinic.salesManRefCode || '',
        insurance_Yes_No: clinic.insurance_Yes_No || '',
        vendor_latlong: clinic.vendor_latlong || '',
      },
    });
    setEditMode(true);
    setEditClinicId(clinic.id);
    await loadPlanAssignment(clinic.id);
    // Fetch existing billing settings for this clinic
    try {
      const res = await apiFetch(`${API_BASE_URL}/clinic-billing/${clinic.id}`);
      const json = await res.json();
      if (json.status === 1 && json.data) {
        setBillingData({
          billing_model: json.data.billing_model || 'subscription',
          subscription_amount: json.data.subscription_amount ?? '',
          subscription_type: json.data.subscription_type || 'monthly',
          commission_rate: json.data.commission_rate ?? '',
          commission_type: json.data.commission_type || 'percentage',
          billing_start_date: json.data.billing_start_date
            ? json.data.billing_start_date.toString().slice(0, 10)
            : '',
          billing_end_date: json.data.billing_end_date
            ? json.data.billing_end_date.toString().slice(0, 10)
            : '',
        });
      } else {
        setBillingData({ billing_model: 'subscription', subscription_amount: '', subscription_type: 'monthly', commission_rate: '', commission_type: 'percentage', billing_start_date: '', billing_end_date: '' });
      }
    } catch (_) {
      setBillingData({ billing_model: 'subscription', subscription_amount: '', subscription_type: 'monthly', commission_rate: '', commission_type: 'percentage', billing_start_date: '', billing_end_date: '' });
    }
    setShowForm('clinic');
  };

  const handleDeleteClinic = async (clinicId) => {
    if (window.confirm('Are you sure you want to delete this clinic?')) {
      try {
        const response = await ClinicService.deleteClinic(clinicId);
        if (response.data.status === 1) {
          showCustomAlert(response.data.msg || 'Clinic Deleted Successfully');
          clinicsRef.current = clinicsRef.current.filter((clinic) => clinic.id !== clinicId);
          setClinics(clinicsRef.current);
          setSelectedClinic(null);
          setRefreshKey((prev) => prev + 1);
        } else {
          showCustomAlert(response.data.msg || 'Failed to delete clinic');
        }
      } catch (error) {
        showCustomAlert(`Error: ${error.message}`);
      }
    }
  };

  const handleEditDoctor = async (doctor) => {
    let clinicIds = [];
    if (Array.isArray(doctor.clinic_ids) && doctor.clinic_ids.length > 0) {
      clinicIds = doctor.clinic_ids.map(Number).filter(Boolean);
    } else if (Array.isArray(doctor.clinic_list) && doctor.clinic_list.length > 0) {
      clinicIds = doctor.clinic_list.map((c) => Number(c.clinicId || c.id)).filter(Boolean);
    } else if (doctor.clinic_id) {
      if (typeof doctor.clinic_id === 'string' && doctor.clinic_id.includes(',')) {
        clinicIds = doctor.clinic_id.split(',').map((s) => parseInt(s.trim())).filter(Boolean);
      } else {
        clinicIds = [parseInt(doctor.clinic_id)].filter(Boolean);
      }
    }

    setFormData({
      ...formData,
      doctor: {
        titleType: doctor.titleType || doctor.title_type || 'Dr.',
        vendorName: doctor.vendorName || doctor.vendor_name || '',
        vendorEmail: doctor.vendorEmail || doctor.vendor_email || '',
        vendorConNumber: doctor.vendorConNumber || doctor.vendor_phone || doctor.vendor_contact_mobile || '',
        specialty_id: doctor.specialty_id || doctor.doctor_speciality_id || '',
        education: doctor.education || doctor.vendor_contact_qualification || '',
        experience: doctor.experience || doctor.vendor_experience || '',
        gender: doctor.gender || doctor.vendor_contact_gender || '',
        regNumber: doctor.vendor_regNumber || doctor.regNumber || doctor.reg_no || doctor.doctor_registration_number || '',
        location: doctor.location || doctor.vendor_location || '',
        address: doctor.address || doctor.vendor_address || '',
        vendor_latlong: doctor.vendor_latlong || doctor.lat_long || '',
        vendor_profile_path: doctor.vendor_profile_path || null,
        license: doctor.license || doctor.licencePath || null,
        authorizedSignature: doctor.authorizedSignature || doctor.authorizedSign || null,
        commercialLicense: doctor.commercialLicense || doctor.commercialLicence || null,
        civilId: doctor.civilId || doctor.civil_id || doctor.civilIdPath || null,
        clinic_id: clinicIds[0] || doctor.clinic_id || '',
        clinic_ids: clinicIds,
        conditions: Array.isArray(doctor.conditions) ? doctor.conditions : [],
        procedures: Array.isArray(doctor.procedures) ? doctor.procedures : [],
        is_committed: doctor.is_committed === 1 || doctor.is_committed === '1' || doctor.commitment_type === 'committed' ? 1 : 0,
        commitment_type: doctor.commitment_type || (doctor.is_committed === 1 ? 'committed' : 'normal'),
        languages: doctor.languages || '',
      },
    });
    setEditMode(true);
    setEditDoctorId(doctor.id);
    await loadPlanAssignment(doctor.id);
    setShowForm('doctor');
  };

  const handleDeleteDoctor = async (doctorId) => {
    if (window.confirm('Are you sure you want to delete this doctor?')) {
      try {
        const response = await DoctorService.deleteDoctor(doctorId);
        if (Number(response.status) === 1) {
          showCustomAlert(response.msg || 'Doctor Deleted Successfully');
          doctorsRef.current = doctorsRef.current.filter((doctor) => doctor.id !== doctorId);
          setDoctors([...doctorsRef.current]);
          setSelectedDoctor(null);
          setRefreshKey((prev) => prev + 1);
        } else {
          showCustomAlert(response.msg || 'Failed to delete doctor');
        }
      } catch (error) {
        showCustomAlert(`Error: ${error.message}`);
      }
    }
  };

  const handleEditPharmacy = async (pharmacy) => {
    setFormData({
      ...formData,
      pharmacy: {
        vendorName: pharmacy.vendorName || '',
        vendorEmail: pharmacy.vendorEmail || '',
        vendorConNumber: pharmacy.vendorConNumber || '',
        location: pharmacy.location || '',
        regNumber: pharmacy.regNumber || '',
        address: pharmacy.address || '',
        vendor_profile_path: pharmacy.vendor_profile_path || null,
        license: null,
        authorizedSignature: null,
        commercialLicense: null,
        clinic_id: pharmacy.clinic_id || '',
      },
    });
    setEditMode(true);
    setEditPharmacyId(pharmacy.id);
    await loadPlanAssignment(pharmacy.id);
    setShowForm('pharmacy');
  };

  const handleLogout = () => {
    Cookies.remove('adminid');
    Cookies.remove('adminname');
    Cookies.remove('role');
    Cookies.remove('token');
    localStorage.removeItem('adminid');
    localStorage.removeItem('adminname');
    localStorage.removeItem('role');
    localStorage.removeItem('token');
    setIsProfileModalOpen(false);
    window.location.href = '/';
  };

  const handleDeletePharmacy = async (pharmacyId) => {
    if (window.confirm('Are you sure you want to delete this pharmacy?')) {
      try {
        const response = await PharmacyService.deletePharmacy(pharmacyId);
        if (response.status === 1) {
          showCustomAlert(response.msg || 'Pharmacy Deleted Successfully');
          pharmaciesRef.current = pharmaciesRef.current.filter(
            (pharmacy) => pharmacy.id !== pharmacyId
          );
          setPharmacies([...pharmaciesRef.current]);
          setSelectedPharmacy(null);
          setRefreshKey((prev) => prev + 1);
        } else {
          showCustomAlert(response.msg || 'Failed to delete pharmacy');
        }
      } catch (error) {
        showCustomAlert(`Error: ${error.message}`);
      }
    }
  };

  const handleEditSupervisor = (supervisor) => {
    setFormData({
      ...formData,
      supervisor: {
        vendorName: supervisor.vendorName || '',
        role: supervisor.role || '',
        department: supervisor.department || '',
        experience: supervisor.experience || '',
        clinic_id: supervisor.clinic_id || '',
        vendorConNumber: supervisor.vendorConNumber || '',
        vendorEmail: supervisor.vendorEmail || '',
        vendor_profile_path: supervisor.vendor_profile_path || null,
        password: supervisor.password || '',
      },
    });
    setEditMode(true);
    setEditSupervisorId(supervisor.id);
    setShowForm('supervisor');
  };

  const handleDeleteSupervisor = async (supervisorId) => {
    if (window.confirm('Are you sure you want to delete this supervisor?')) {
      const adminid = Cookies.get('adminid');
      try {
        const response = await SupervisorService.deleteSupervisor(supervisorId, adminid);
        if (response.data[0].status === 1) {
          showCustomAlert(response.data[0].msg || 'Supervisor Deleted Successfully');
          supervisorsRef.current = supervisorsRef.current.filter(
            (supervisor) => supervisor.id !== supervisorId
          );
          setSupervisors([...supervisorsRef.current]);
          setSelectedSupervisor(null);
          setRefreshKey((prev) => prev + 1);
        } else {
          showCustomAlert(response.data[0].msg || 'Failed to delete supervisor');
        }
      } catch (error) {
        showCustomAlert(`Error: ${error.message}`);
      }
    }
  };

  const renderOverview = () => {
    const approvedClinics = clinics.filter(c => c.vendor_approve_status === 'A').length;
    const pendingClinics = clinics.filter(c => c.vendor_approve_status !== 'A').length;
    const approvedDoctors = doctors.filter(d => d.vendor_approve_status === 'A').length;
    const pendingDoctors = doctors.filter(d => d.vendor_approve_status !== 'A').length;
    const approvedPharmacies = pharmacies.filter(p => p.vendor_approve_status === 'A').length;
    const pendingPharmacies = pharmacies.filter(p => p.vendor_approve_status !== 'A').length;
    const totalPending = pendingClinics + pendingDoctors + pendingPharmacies;

    const statCards = [
      {
        label: 'Total Clinics',
        value: clinics.length,
        approved: approvedClinics,
        pending: pendingClinics,
        icon: Building2,
        color: 'teal',
        section: 'clinic',
      },
      {
        label: 'Total Doctors',
        value: doctors.length,
        approved: approvedDoctors,
        pending: pendingDoctors,
        icon: Users,
        color: 'indigo',
        section: 'doctor',
      },
      {
        label: 'Total Pharmacies',
        value: pharmacies.length,
        approved: approvedPharmacies,
        pending: pendingPharmacies,
        icon: Pill,
        color: 'emerald',
        section: 'pharmacy',
      },
      {
        label: 'Total Supervisors',
        value: supervisors.length,
        approved: supervisors.filter(s => s.vendor_approve_status === 'A').length,
        pending: supervisors.filter(s => s.vendor_approve_status !== 'A').length,
        icon: UserCheck,
        color: 'violet',
        section: 'supervisor',
      },
    ];

    const colorMap = {
      teal: { bg: 'bg-teal-50', text: 'text-teal-700', icon: 'bg-teal-100', border: 'border-teal-100' },
      indigo: { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'bg-indigo-100', border: 'border-indigo-100' },
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'bg-emerald-100', border: 'border-emerald-100' },
      violet: { bg: 'bg-violet-50', text: 'text-violet-700', icon: 'bg-violet-100', border: 'border-violet-100' },
    };

    // Collect all pending items across all categories
    const pendingItems = [
      ...clinics.filter(c => c.vendor_approve_status !== 'A').map(c => ({
        name: c.vendor_name || 'Unnamed Clinic',
        type: 'Clinic',
        status: c.vendor_approve_status || 'Pending',
        section: 'clinic',
        item: c,
      })),
      ...doctors.filter(d => d.vendor_approve_status !== 'A').map(d => ({
        name: d.vendorName || 'Unnamed Doctor',
        type: 'Doctor',
        status: d.vendor_approve_status || 'Pending',
        section: 'doctor',
        item: d,
      })),
      ...pharmacies.filter(p => p.vendor_approve_status !== 'A').map(p => ({
        name: p.vendorName || 'Unnamed Pharmacy',
        type: 'Pharmacy',
        status: p.vendor_approve_status || 'Pending',
        section: 'pharmacy',
        item: p,
      })),
    ];

    return (
      <div className="content-section">
        {/* Header */}
        <div className="content-header" style={{ marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.2rem' }}>
              Platform Overview
            </h1>
            <p style={{ fontSize: '0.8rem', color: '#64748b' }}>
              Real-time snapshot of all entities on the platform
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '0.75rem', padding: '0.5rem 0.9rem' }}>
            <Activity size={14} style={{ color: '#16a34a' }} />
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#16a34a' }}>Platform Online</span>
          </div>
        </div>

        {/* Stat cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {statCards.map((card) => {
            const Icon = card.icon;
            const c = colorMap[card.color];
            return (
              <div
                key={card.section}
                onClick={() => {
                  setActiveSection(card.section);
                  setSelectedClinic(null); setSelectedDoctor(null);
                  setSelectedPharmacy(null); setSelectedSupervisor(null);
                  setShowForm(null);
                }}
                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '1rem', padding: '1.25rem', cursor: 'pointer', transition: 'box-shadow 0.15s', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.05)'}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '0.6rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: c.icon }}>
                    <Icon size={20} style={{ color: c.text.replace('text-', '').includes('teal') ? '#0d9488' : c.text.includes('indigo') ? '#4f46e5' : c.text.includes('emerald') ? '#059669' : '#7c3aed' }} />
                  </div>
                  <ChevronRight size={16} style={{ color: '#94a3b8' }} />
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{card.value}</div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '0.25rem', marginBottom: '0.75rem', fontWeight: 600 }}>{card.label}</div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.7rem', background: '#f0fdf4', color: '#16a34a', borderRadius: '999px', padding: '0.2rem 0.6rem', fontWeight: 600 }}>
                    ✓ {card.approved} Active
                  </span>
                  {card.pending > 0 && (
                    <span style={{ fontSize: '0.7rem', background: '#fef3c7', color: '#d97706', borderRadius: '999px', padding: '0.2rem 0.6rem', fontWeight: 600 }}>
                      ⏳ {card.pending} Pending
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pending Approvals */}
        {pendingItems.length > 0 && (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '1rem', padding: '1.25rem', marginBottom: '2rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <AlertCircle size={18} style={{ color: '#d97706' }} />
              <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                Pending Approvals
                <span style={{ marginLeft: '0.5rem', background: '#fef3c7', color: '#d97706', fontSize: '0.7rem', fontWeight: 700, borderRadius: '999px', padding: '0.15rem 0.55rem' }}>
                  {totalPending}
                </span>
              </h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.6rem' }}>
              {pendingItems.map((p, idx) => (
                <div
                  key={idx}
                  onClick={() => {
                    setActiveSection(p.section);
                    setSelectedClinic(null); setSelectedDoctor(null);
                    setSelectedPharmacy(null); setSelectedSupervisor(null);
                    setShowForm(null);
                    // Navigate into the detail view
                    if (p.section === 'clinic') setSelectedClinic(p.item);
                    else if (p.section === 'doctor') setSelectedDoctor(p.item);
                    else if (p.section === 'pharmacy') setSelectedPharmacy(p.item);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '0.75rem', padding: '0.75rem 1rem', cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fffbeb'}
                >
                  <div style={{ width: 32, height: 32, borderRadius: '0.5rem', background: '#fde68a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Clock size={15} style={{ color: '#92400e' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#92400e', fontWeight: 600 }}>{p.type} · {p.status}</div>
                  </div>
                  <ChevronRight size={14} style={{ color: '#d97706', flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick actions */}
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '1rem', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <TrendingUp size={18} style={{ color: '#0d9488' }} />
            <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>Quick Actions</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.75rem' }}>
            {[
              { label: 'Register Clinic', section: 'clinic', icon: Building2 },
              { label: 'Register Doctor', section: 'doctor', icon: Users },
              { label: 'Register Pharmacy', section: 'pharmacy', icon: Pill },
              { label: 'Add Supervisor', section: 'supervisor', icon: UserCheck },
            ].map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.section}
                  onClick={() => {
                    setActiveSection(action.section);
                    setSelectedClinic(null); setSelectedDoctor(null);
                    setSelectedPharmacy(null); setSelectedSupervisor(null);
                    setShowForm(action.section);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '0.75rem', padding: '0.75rem 1rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', color: '#0f766e', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#ccfbf1'}
                  onMouseLeave={e => e.currentTarget.style.background = '#f0fdfa'}
                >
                  <Icon size={16} />
                  {action.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (activeSection === 'overview') return renderOverview();
    if (activeSection === 'analytics') return (
      <Analytics
        clinics={clinics}
        doctors={doctors}
        pharmacies={pharmacies}
        supervisors={supervisors}
      />
    );
    if (activeSection === 'speciality') return <SpecialityMaster />;
    if (activeSection === 'pharmacy-category') return <PharmacyCategoryMaster />;
    if (activeSection === 'labtest-category') return <LabTestCategoryMaster />;
    if (activeSection === 'homecare-category') return <HomeCareCategoryMaster />;
    if (activeSection === 'roles') return <RoleMaster />;
    if (activeSection === 'currencies') return <CurrencyMaster showCustomAlert={showCustomAlert} />;
    if (activeSection === 'locations') return <LocationMaster showCustomAlert={showCustomAlert} />;
    if (activeSection === 'revenue') return <RevenueReport />;
    if (activeSection === 'users') return <UserManagement />;
    if (activeSection === 'settings') return <Settings />;

    // 2. Lead Generation Management
    if (activeSection === 'leads-all') return <LeadsManagement activeSubTab="all" showCustomAlert={showCustomAlert} />;
    if (activeSection === 'leads-doctor') return <LeadsManagement activeSubTab="doctor" showCustomAlert={showCustomAlert} />;
    if (activeSection === 'leads-pharmacy') return <LeadsManagement activeSubTab="pharmacy" showCustomAlert={showCustomAlert} />;
    if (activeSection === 'leads-lab') return <LeadsManagement activeSubTab="lab" showCustomAlert={showCustomAlert} />;
    if (activeSection === 'leads-homecare') return <LeadsManagement activeSubTab="homecare" showCustomAlert={showCustomAlert} />;
    if (activeSection === 'leads-delivery' || activeSection === 'delivery-logs') return <LeadsManagement activeSubTab="delivery" showCustomAlert={showCustomAlert} />;
    if (activeSection === 'api-integrations') return <VendorIntegrationManagement vendors={[...clinics, ...doctors, ...pharmacies]} showCustomAlert={showCustomAlert} />;

    // 6. Subscription & Module Management
    if (activeSection === 'subscription-plans') return <SubscriptionModuleMaster initialTab="plans" showCustomAlert={showCustomAlert} clinics={clinics} doctors={doctors} pharmacies={pharmacies} />;
    if (activeSection === 'module-subscriptions') return <SubscriptionModuleMaster initialTab="vendor_subs" showCustomAlert={showCustomAlert} clinics={clinics} doctors={doctors} pharmacies={pharmacies} />;

    // 4 & 7. Vendor Access Models & Integrations
    if (activeSection === 'vendor-access-models' || activeSection === 'api-vendors') {
      return (
        <div className="leads-management-wrapper">
          <div className="leads-table-card">
            <div style={{ padding: '1.25rem', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Organisation Access & Integration Control</h3>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#64748b' }}>Configure whether each vendor accesses Digi Living via Lead Portal Only, External API, or Subscribed CMS Modules.</p>
            </div>
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Organisation Name</th>
                  <th>Type</th>
                  <th>Current Access Model</th>
                  <th>Lead Delivery Target</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {[...clinics, ...pharmacies].map(v => (
                  <tr key={v.id}>
                    <td><strong>{v.vendorName || v.vendor_name}</strong></td>
                    <td>{v.vendorTypeId === 3 ? 'Clinic' : 'Pharmacy'}</td>
                    <td>
                      <span className={`delivery-badge badge-${v.business_model || 'cms_only'}`}>
                        {v.business_model === 'leads_only' ? 'Lead Portal Only' : v.business_model === 'api_only' ? 'API Integration' : 'Digi Living CMS'}
                      </span>
                    </td>
                    <td>{v.lead_delivery_mode || 'cms_module'}</td>
                    <td>
                      <button 
                        className="view-lead-btn" 
                        onClick={() => setAccessModalVendor(v)}
                      >
                        <SlidersHorizontal size={14} /> Configure Model
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    if (isLoading) {
      return <div className="content-section">Loading...</div>;
    }
    if (showForm === 'clinic') {
      return (
        <ClinicForm
          formData={formData.clinic}
          handleFormChange={(field, value) => handleFormChange('clinic', field, value)}
          handleFileChange={(field, event) => handleFileChange('clinic', field, event)}
          handleSubmit={() => handleFormSubmit('clinic')}
          onCancel={() => {
            setShowForm(null);
            setEditMode(false);
            setEditClinicId(null);
            setBillingData({ billing_model: 'subscription', subscription_amount: '', subscription_type: 'monthly', commission_rate: '', commission_type: 'percentage', billing_start_date: '', billing_end_date: '' });
            setFormData((prev) => ({
              ...prev,
              clinic: {
                vendorTypeId: 3,
                vendorName: '',
                vendorEmail: '',
                vendorConNumber: '',
                location: '',
                regNumber: '',
                address: '',
                LicenceExpDate: '',
                vendor_profile_path: null,
                licencePath: null,
                authorizedSign: null,
                commercialLicence: null,
                civilIdPath: null,
                pharmacydataArray: [],
                doctorList: [],
                insuranceList: '',
                salesManRefCode: '',
                insurance_Yes_No: '',
                vendor_latlong: '',
                password: '',
              },
            }));
          }}
          editMode={editMode}
          billingData={billingData}
          handleBillingChange={handleBillingChange}
          subscriptionPanel={<PlanAssignmentPanel vendorType="clinic" plans={subscriptionPlans} value={planAssignment} onChange={handlePlanAssignmentChange} editMode={editMode} />}
        />
      );
    }
    if (showForm === 'doctor') {
      return (
        <DoctorForm
          formData={formData.doctor}
          handleFormChange={(field, value) => handleFormChange('doctor', field, value)}
          handleFileChange={(field, event) => handleFileChange('doctor', field, event)}
          handleSubmit={() => handleFormSubmit('doctor')}
          onCancel={() => {
            setShowForm(null);
            setEditMode(false);
            setEditDoctorId(null);
            setFormData((prev) => ({
              ...prev,
              doctor: {
                titleType: 'Dr.',
                vendorName: '',
                vendorEmail: '',
                vendorConNumber: '',
                specialty_id: '',
                education: '',
                experience: '',
                gender: '',
                regNumber: '',
                location: '',
                address: '',
                vendor_profile_path: null,
                license: null,
                authorizedSignature: null,
                commercialLicense: null,
                civilId: null,
                clinic_id: '',
                clinic_ids: [],
                languages: '',
                password: '',
              },
            }));
          }}
          editMode={editMode}
          subscriptionPanel={<PlanAssignmentPanel vendorType="doctor" plans={subscriptionPlans} value={planAssignment} onChange={handlePlanAssignmentChange} editMode={editMode} />}
        />
      );
    }
    if (showForm === 'pharmacy') {
      return (
        <PharmacyForm
          formData={formData.pharmacy}
          handleFormChange={(field, value) => handleFormChange('pharmacy', field, value)}
          handleFileChange={(field, event) => handleFileChange('pharmacy', field, event)}
          handleSubmit={() => handleFormSubmit('pharmacy')}
          onCancel={() => {
            setShowForm(null);
            setEditMode(false);
            setEditPharmacyId(null);
            setFormData((prev) => ({
              ...prev,
              pharmacy: {
                vendorName: '',
                vendorEmail: '',
                vendorConNumber: '',
                location: '',
                regNumber: '',
                address: '',
                vendor_profile_path: null,
                license: null,
                authorizedSignature: null,
                commercialLicense: null,
                clinic_id: '',
                password: '',
              },
            }));
          }}
          editMode={editMode}
          subscriptionPanel={<PlanAssignmentPanel vendorType="pharmacy" plans={subscriptionPlans} value={planAssignment} onChange={handlePlanAssignmentChange} editMode={editMode} />}
        />
      );
    }
    if (showForm === 'lab' || showForm === 'homecare') {
      const vendorType = showForm;
      return <ServiceVendorForm
        vendorType={vendorType}
        formData={formData[vendorType]}
        onChange={(field, value) => handleFormChange(vendorType, field, value)}
        onSubmit={() => handleFormSubmit(vendorType)}
        onCancel={() => setShowForm(null)}
        subscriptionPanel={<PlanAssignmentPanel vendorType={vendorType} plans={subscriptionPlans} value={planAssignment} onChange={handlePlanAssignmentChange} />}
      />;
    }
    if (showForm === 'supervisor') {
      return (
        <SupervisorForm
          formData={formData.supervisor}
          handleFormChange={(field, value) => handleFormChange('supervisor', field, value)}
          handleFileChange={(field, event) => handleFileChange('supervisor', field, event)}
          handleSubmit={() => handleFormSubmit('supervisor')}
          onCancel={() => {
            setShowForm(null);
            setEditMode(false);
            setEditSupervisorId(null);
            setFormData((prev) => ({
              ...prev,
              supervisor: {
                vendorName: '',
                role: 'supervisor',
                department: '',
                experience: '',
                clinic_id: '',
                vendorConNumber: '',
                vendorEmail: '',
                vendor_profile_path: null,
                password: '',
              },
            }));
          }}
          editMode={editMode}
        />
      );
    }
    if (selectedClinic) {
      return (
        <ClinicDetails
          selectedClinic={selectedClinic}
          linkedDoctors={getLinkedDoctors(selectedClinic.id)}
          linkedPharmacies={getLinkedPharmacies(selectedClinic.id)}
          onDoctorClick={setSelectedDoctor}
          onPharmacyClick={setSelectedPharmacy}
          onBack={() => setSelectedClinic(null)}
          onEdit={() => handleEditClinic(selectedClinic)}
          onDelete={() => handleDeleteClinic(selectedClinic.id)}
        />
      );
    }
    if (selectedDoctor) {
      return (
        <DoctorDetails
          selectedDoctor={selectedDoctor}
          clinics={clinics}
          onBack={() => setSelectedDoctor(null)}
          onEdit={() => handleEditDoctor(selectedDoctor)}
          onDelete={() => handleDeleteDoctor(selectedDoctor.id)}
        />
      );
    }
    if (selectedPharmacy) {
      return (
        <PharmacyDetails
          selectedPharmacy={selectedPharmacy}
          clinics={clinics}
          onBack={() => setSelectedPharmacy(null)}
          onEdit={() => handleEditPharmacy(selectedPharmacy)}
          onDelete={() => handleDeletePharmacy(selectedPharmacy.id)}
        />
      );
    }
    if (selectedSupervisor) {
      return (
        <SupervisorDetails
          selectedSupervisor={selectedSupervisor}
          clinics={clinics}
          onBack={() => setSelectedSupervisor(null)}
          onEdit={() => handleEditSupervisor(selectedSupervisor)}
          onDelete={() => handleDeleteSupervisor(selectedSupervisor.id)}
        />
      );
    }

    const data = filteredData();
    return (
      <div className="content-section">
        <div className="content-header">
          <div>
            <h1 style={{ margin: 0, textTransform: 'capitalize' }}>
              {activeSection === 'all-vendors' 
                ? 'All Vendors Directory' 
                : activeSection === 'pending-registrations' 
                ? 'Pending Registrations & Approvals' 
                : activeSection === 'lab'
                ? 'Diagnostic Labs Management'
                : activeSection === 'homecare'
                ? 'Home Care Management'
                : `${activeSection} Management`}
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              {activeSection === 'pending-registrations'
                ? 'Review and verify newly submitted clinics, doctors, and pharmacy registrations.'
                : activeSection === 'all-vendors'
                ? 'Consolidated directory of all healthcare providers across the platform.'
                : activeSection === 'lab'
                ? 'Manage diagnostic laboratory organisations and their CMS access.'
                : activeSection === 'homecare'
                ? 'Manage home-care provider organisations and their CMS access.'
                : `Manage and configure ${activeSection} profiles.`}
            </p>
          </div>
          <div className="header-actions">
            <div className="search-container">
              <Search size={20} />
              <input
                type="text"
                placeholder={
                  activeSection === 'pending-registrations'
                    ? 'Search pending approvals...'
                    : activeSection === 'all-vendors'
                    ? 'Search all vendors...'
                    : `Search ${activeSection}s...`
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {userRole === 'SuperAdmin' && (activeSection === 'clinic' || activeSection === 'doctor') && (
              <button
                className="create-btn flex items-center gap-1.5"
                style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)' }}
                onClick={() => {
                  setBulkModalType(activeSection);
                  setIsBulkModalOpen(true);
                }}
              >
                <FileSpreadsheet size={16} />
                Bulk Upload & Update
              </button>
            )}
            {userRole === 'SuperAdmin' && activeSection !== 'pending-registrations' && (
              <button className="create-btn" onClick={handleCreateNew}>
                Create New
              </button>
            )}
          </div>
        </div>

        {data.length === 0 ? (
          <div style={{
            background: '#ffffff',
            borderRadius: '12px',
            padding: '3rem 2rem',
            textAlign: 'center',
            border: '1px solid #e2e8f0',
            marginTop: '1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: '#ecfdf5',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}>
              <CheckCircle2 size={28} />
            </div>
            <h3 style={{ margin: '0 0 0.5rem', color: '#0f172a', fontSize: '1.1rem' }}>
              {activeSection === 'pending-registrations' 
                ? 'No Pending Approvals' 
                : 'No Records Found'}
            </h3>
            <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
              {activeSection === 'pending-registrations'
                ? 'All submitted healthcare organisations and practitioners are currently approved and active on the platform.'
                : 'No data matches your current search or filter criteria.'}
            </p>
          </div>
        ) : (
          <>
          <OrganisationDataGrid
            data={data}
            activeSection={activeSection}
            clinics={clinics}
            page={organisationPage}
            pageSize={organisationPageSize}
            onPageChange={setOrganisationPage}
            onPageSizeChange={setOrganisationPageSize}
            onOpen={(item) => {
              const targetSection = item.vendorTypeLabel ? item.vendorTypeLabel.toLowerCase() : activeSection;
              if (targetSection === 'clinic') setSelectedClinic(item);
              else if (targetSection === 'doctor') setSelectedDoctor(item);
              else if (targetSection === 'pharmacy') setSelectedPharmacy(item);
              else if (targetSection === 'supervisor') setSelectedSupervisor(item);
            }}
          />
          {false && <div className="cards-grid">
            {data.map((item) => {
            const isApproved = item?.vendor_approve_status === 'A';
            const name = item.vendor_name || item.vendorName || 'Unnamed';
            const email = item.vendor_email || item.vendorEmail || 'N/A';
            const phone = item.vendor_phone || item.vendorConNumber || 'N/A';
            const profileImage = item.vendor_profile_path;

            return (
              <div
                key={item?.id || Math.random()}
                className="admin-mgmt-card"
                onClick={() => {
                  const targetSection = item.vendorTypeLabel ? item.vendorTypeLabel.toLowerCase() : activeSection;
                  if (targetSection === 'clinic') setSelectedClinic(item);
                  else if (targetSection === 'doctor') setSelectedDoctor(item);
                  else if (targetSection === 'pharmacy') setSelectedPharmacy(item);
                  else if (targetSection === 'supervisor') setSelectedSupervisor(item);
                }}
              >
                <div className="admin-mgmt-card-header">
                  <div className="flex items-center gap-3">
                    {profileImage ? (
                      <img
                        src={profileImage}
                        alt={name}
                        className="w-11 h-11 rounded-xl object-cover border border-slate-100 shadow-sm"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center font-bold text-base border border-teal-100">
                        {name[0]?.toUpperCase() || 'M'}
                      </div>
                    )}
                    <div>
                      <h3 className="text-base font-bold text-slate-800 leading-snug">{name}</h3>
                      <p className="text-xs text-slate-400 capitalize">{item.vendorTypeLabel || activeSection}</p>
                    </div>
                  </div>
                  <span className={`status-pill ${isApproved ? 'approved' : 'pending'}`}>
                    <span className="dot" />
                    {isApproved ? 'Approved' : (item?.vendor_approve_status || 'Pending')}
                  </span>
                </div>

                <div className="admin-mgmt-card-body">
                  <div className="data-row">
                    <Mail size={14} className="data-icon" />
                    <span className="data-text">{email}</span>
                  </div>
                  <div className="data-row">
                    <Phone size={14} className="data-icon" />
                    <span className="data-text">{phone}</span>
                  </div>

                  {activeSection === 'clinic' && (
                    <>
                      <div className="data-row">
                        <MapPin size={14} className="data-icon" />
                        <span className="data-text">{item.vendor_location || item.vendor_address || 'N/A'}</span>
                      </div>
                      {item.reg_no && (
                        <div className="data-row">
                          <ShieldCheck size={14} className="data-icon" />
                          <span className="data-text">Reg: {item.reg_no}</span>
                        </div>
                      )}
                      {/* Clinic Billing Summary Badge */}
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                        {item.billing_model === 'commission' ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '12px', background: '#fdf4ff', color: '#7e22ce', border: '1px solid #f3e8ff' }}>
                              Commission: {item.commission_type === 'percentage' ? `${item.commission_rate ?? 0}%` : `₹${item.commission_rate ?? 0}/booking`}
                            </span>
                          </div>
                        ) : (
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '3px' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '12px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #dbeafe' }}>
                                Subscription: ₹{Number(item.subscription_amount || 0).toLocaleString()} / {item.subscription_type || 'monthly'}
                              </span>
                            </div>
                            {(item.billing_start_date || item.billing_end_date) && (
                              <div style={{ fontSize: '10px', color: '#64748b', fontWeight: '500' }}>
                                {item.billing_start_date ? item.billing_start_date.slice(0, 10) : '—'} to {item.billing_end_date ? item.billing_end_date.slice(0, 10) : '—'}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {activeSection === 'doctor' && (
                    <>
                      <div className="data-row">
                        <Award size={14} className="data-icon" />
                        <span className="data-text">{item.specialty || item.education || 'Doctor'}</span>
                      </div>
                      {item.clinic && (
                        <div className="data-row">
                          <Building2 size={14} className="data-icon" />
                          <span className="data-text">Clinic: {item.clinic}</span>
                        </div>
                      )}
                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f1f5f9' }}>
                        {item.is_committed === 1 || item.commitment_type === 'committed' ? (
                          <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '12px', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            <ShieldCheck size={12} /> Assured Doctor • Committed
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '12px', background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0' }}>
                            Normal Doctor
                          </span>
                        )}
                      </div>
                    </>
                  )}

                  {activeSection === 'pharmacy' && (
                    <>
                      <div className="data-row">
                        <MapPin size={14} className="data-icon" />
                        <span className="data-text">{item.location || item.vendor_location || item.address || 'N/A'}</span>
                      </div>
                      {item.clinic && (
                        <div className="data-row">
                          <Building2 size={14} className="data-icon" />
                          <span className="data-text">Clinic: {item.clinic}</span>
                        </div>
                      )}
                    </>
                  )}

                  {activeSection === 'supervisor' && (
                    <>
                      <div className="data-row">
                        <Award size={14} className="data-icon" />
                        <span className="data-text">{item.role || 'Supervisor'} &bull; {item.department || 'General'}</span>
                      </div>
                      {(item.clinic || item.clinic_id) && (
                        <div className="data-row">
                          <Building2 size={14} className="data-icon" />
                          <span className="data-text">Clinic: {clinics.find((c) => c.id === item.clinic_id)?.vendor_name || item.clinic}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="admin-mgmt-card-footer">
                  <span className="view-details-btn">
                    View Details <ChevronRight size={14} />
                  </span>
                </div>
              </div>
            );
          })}
        </div>}
        </>
        )}
      </div>
    );
  };

  return (
    <div className="dashboard">
      {sidebarOpen && <div className="overlay" onClick={() => setSidebarOpen(false)}></div>}
      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-white font-bold text-lg leading-tight">Super Admin</h2>
              <span className="text-teal-400 text-xs font-semibold uppercase tracking-wider">Management Portal</span>
            </div>
          </div>
          <button className="close-btn" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {sidebarGroups.map((group) => {
            const isOpen = openGroups[group.id] ?? true;
            return (
              <div key={group.id} className="sidebar-group">
                <button
                  type="button"
                  className="sidebar-group-header"
                  onClick={() => toggleGroup(group.id)}
                >
                  <span className="sidebar-group-title">{group.title}</span>
                  <ChevronDown
                    size={14}
                    className={`sidebar-group-chevron ${isOpen ? 'rotated' : ''}`}
                  />
                </button>
                {isOpen && (
                  <div className="sidebar-group-items">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;
                      return (
                        <button
                          key={item.id}
                          className={`nav-item ${isActive ? 'active' : ''}`}
                          onClick={() => {
                            setActiveSection(item.id);
                            setSelectedClinic(null);
                            setSelectedDoctor(null);
                            setSelectedPharmacy(null);
                            setSelectedSupervisor(null);
                            setShowForm(null);
                            setSidebarOpen(false);
                            setAlert({ show: false, message: '' });
                          }}
                        >
                          <Icon size={18} className="nav-icon" />
                          <span className="nav-label">{item.label}</span>
                          {typeof item.count === 'number' && (
                            <span className={`nav-badge ${isActive ? 'nav-badge-active' : ''}`}>
                              {item.count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>
      <div className="main-content">
        <div className="top-bar">
          <div className="flex items-center gap-4">
            <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
              <Menu size={22} />
            </button>
            <div>
              <h1 className="text-xl font-extrabold text-slate-800 tracking-tight">Super Admin Platform</h1>
              <p className="text-xs text-slate-400 font-medium">Control all clinics, doctors, pharmacies &amp; supervisors</p>
            </div>
          </div>
          <div className="relative">
            <button
              className="profile-btn flex items-center gap-3 py-1.5 px-3 rounded-full hover:bg-slate-100 border border-slate-200/80 transition-all shadow-sm"
              onClick={() => setIsProfileModalOpen(!isProfileModalOpen)}
            >
              <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                {(Cookies.get('adminname') || 'A')[0].toUpperCase()}
              </div>
              <div className="text-left hidden md:block">
                <p className="text-xs font-bold text-slate-700 leading-none">{Cookies.get('adminname') || 'Admin'}</p>
                <p className="text-[10px] text-teal-600 font-semibold uppercase mt-0.5">{userRole || 'SuperAdmin'}</p>
              </div>
            </button>
            {isProfileModalOpen && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-2xl shadow-2xl z-50 overflow-hidden border border-slate-100">
                <button
                  className="absolute top-3 right-3 w-6 h-6 rounded-full border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 transition-all z-10"
                  onClick={() => setIsProfileModalOpen(false)}
                >
                  <X size={12} />
                </button>
                <div className="px-6 py-8 text-center">
                  <div className="mb-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto bg-teal-600 text-white font-black text-2xl shadow-md">
                      {(Cookies.get('adminname') || 'A')[0].toUpperCase()}
                    </div>
                  </div>
                  <h2 className="text-base font-bold text-slate-800 mb-1">
                    <span>{Cookies.get('adminname') || 'Admin'}</span>
                  </h2>
                  <p className="text-xs text-teal-600 font-semibold uppercase tracking-wider mb-6">{userRole || 'SuperAdmin'}</p>
                  <button
                    className="w-full bg-red-50 hover:bg-red-500 text-red-600 hover:text-white font-semibold py-2.5 px-4 rounded-xl text-sm transition-all duration-200 flex items-center justify-center gap-2 border border-red-100 hover:border-red-500 shadow-sm"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {alert.show && (
          <CustomAlert
            message={alert.message}
            onClose={() => setAlert({ show: false, message: '' })}
          />
        )}
        <BulkUploadModal
          isOpen={isBulkModalOpen}
          onClose={() => setIsBulkModalOpen(false)}
          type={bulkModalType}
          existingList={bulkModalType === 'clinic' ? clinics : doctors}
          specialities={specialitiesRef.current}
          clinics={clinics}
          onSuccess={() => {
            setRefreshKey((prev) => prev + 1);
          }}
        />
        <VendorAccessModal 
          isOpen={Boolean(accessModalVendor)}
          vendor={accessModalVendor}
          onClose={() => setAccessModalVendor(null)}
          showCustomAlert={showCustomAlert}
        />
        {showVendorTypeSelector && (
          <div className="vendor-module-overlay" role="presentation" onMouseDown={() => setShowVendorTypeSelector(false)}>
            <section className="vendor-module-dialog" role="dialog" aria-modal="true" aria-labelledby="vendor-module-title" onMouseDown={(event) => event.stopPropagation()}>
              <header className="vendor-module-header">
                <div>
                  <span className="vendor-module-eyebrow">Healthcare Organisations</span>
                  <h2 id="vendor-module-title">Create New Vendor</h2>
                  <p>Select the CMS module for the healthcare provider you want to register.</p>
                </div>
                <button type="button" className="vendor-module-close" aria-label="Close" onClick={() => setShowVendorTypeSelector(false)}><X size={20} /></button>
              </header>
              <div className="vendor-module-grid">
                {vendorCreationModules.map((module) => {
                  const Icon = module.icon;
                  return (
                    <button type="button" key={module.id} className={`vendor-module-option ${module.id}`} onClick={() => selectVendorCreationModule(module.id)}>
                      <span className="vendor-module-icon"><Icon size={24} /></span>
                      <span className="vendor-module-copy"><strong>{module.label}</strong><small>{module.description}</small></span>
                      <ChevronRight size={18} className="vendor-module-arrow" />
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        )}
        {renderContent()}
      </div>
    </div>
  );
};

export default AdminDashboard;
